import { __assign, __spreadArray } from "tslib";
import React from 'react';
import { STATIC_EXECUTION_CONTEXT } from '../constants';
import GlobalStyle from '../models/GlobalStyle';
import { useStyleSheetContext } from '../models/StyleSheetManager';
import { ThemeContext } from '../models/ThemeProvider';
import { checkDynamicCreation } from '../utils/checkDynamicCreation';
import determineTheme from '../utils/determineTheme';
import generateComponentId from '../utils/generateComponentId';
import css from './css';
export default function createGlobalStyle(strings) {
    var interpolations = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        interpolations[_i - 1] = arguments[_i];
    }
    var rules = css.apply(void 0, __spreadArray([strings], interpolations, false));
    var styledComponentId = "sc-global-".concat(generateComponentId(JSON.stringify(rules)));
    var globalStyle = new GlobalStyle(rules, styledComponentId);
    if (process.env.NODE_ENV !== 'production') {
        checkDynamicCreation(styledComponentId);
    }
    var GlobalStyleComponent = function (props) {
        var ssc = useStyleSheetContext();
        var theme = React.useContext(ThemeContext);
        var instanceRef = React.useRef(ssc.styleSheet.allocateGSInstance(styledComponentId));
        var instance = instanceRef.current;
        if (process.env.NODE_ENV !== 'production' && React.Children.count(props.children)) {
            console.warn("The global style component ".concat(styledComponentId, " was given child JSX. createGlobalStyle does not render children."));
        }
        if (process.env.NODE_ENV !== 'production' &&
            rules.some(function (rule) { return typeof rule === 'string' && rule.indexOf('@import') !== -1; })) {
            console.warn("Please do not use @import CSS syntax in createGlobalStyle at this time, as the CSSOM APIs we use in production do not handle it well. Instead, we recommend using a library such as react-helmet to inject a typical <link> meta tag to the stylesheet, or simply embedding it manually in your index.html <head> section for a simpler app.");
        }
        if (ssc.styleSheet.server) {
            renderStyles(instance, props, ssc.styleSheet, theme, ssc.stylis);
        }
        if (!__SERVER__) {
            React.useLayoutEffect(function () {
                if (!ssc.styleSheet.server) {
                    renderStyles(instance, props, ssc.styleSheet, theme, ssc.stylis);
                    return function () { return globalStyle.removeStyles(instance, ssc.styleSheet); };
                }
            }, [instance, props, ssc.styleSheet, theme, ssc.stylis]);
        }
        return null;
    };
    function renderStyles(instance, props, styleSheet, theme, stylis) {
        if (globalStyle.isStatic) {
            globalStyle.renderStyles(instance, STATIC_EXECUTION_CONTEXT, styleSheet, stylis);
        }
        else {
            var context = __assign(__assign({}, props), { theme: determineTheme(props, theme, GlobalStyleComponent.defaultProps) });
            globalStyle.renderStyles(instance, context, styleSheet, stylis);
        }
    }
    return React.memo(GlobalStyleComponent);
}
//# sourceMappingURL=createGlobalStyle.js.map